"use client";

import styles from "@/styles/category/index.module.scss";
import Image from "next/image";
import { useState, useEffect } from "react";
import { Popup } from "react-vant";
import { ArrowUp, ArrowDown } from "@react-vant/icons";

interface FilterOption {
  type: string;
  // value: 用于排序等简单值；item: 用于复杂对象（如分类项）
  value?: string;
  item?: any;
}

interface FilterBoxProps {
  onSortChange: (option: FilterOption) => void;
  sidebarFilter?: any[];
  showFilter?: boolean; // 控制 filter 图标的显示，默认为 true
  categoryNameFilter?: string; // 当前选中的分类名称（用于设置默认选中状态）
  showFreeShipping?: boolean; // 控制是否显示 Free Shipping 选项，默认为 true
  defaultSort?: {
    sortBy?: string;
    sortDirection?: string;
  }; // 默认排序配置
}

export default function FilterBox({
  onSortChange,
  sidebarFilter,
  showFilter = true, // 默认显示 filter
  categoryNameFilter = "", // 当前选中的分类名称
  showFreeShipping = true, // 默认显示 Free Shipping
  defaultSort, // 默认排序配置
}: FilterBoxProps) {
  // 根据 defaultSort 获取初始选中状态
  const getInitialSelectedFilter = (): FilterOption => {
    if (!defaultSort?.sortBy) {
      return { type: "sort", value: "bestMatch" };
    }

    const sortBy = defaultSort.sortBy.toLowerCase();
    switch (sortBy) {
      case "addtime":
        return { type: "sort", value: "new-arrivals" };
      case "popularity":
        // free-shipping 页面：默认选中 icon-all
        return { type: "sort", value: "bestMatch" };
      case "price":
        return {
          type: "sort",
          value:
            defaultSort.sortDirection?.toLowerCase() === "asc"
              ? "price-low-to-high"
              : "price-high-to-low",
        };
      case "bestmatch":
      default:
        return { type: "sort", value: "bestMatch" };
    }
  };

  // 跟踪当前选中的筛选项
  const [selectedFilter, setSelectedFilter] = useState<FilterOption | null>(
    getInitialSelectedFilter()
  );
  const [visible, setVisible] = useState(false);
  // 跟踪每个筛选项的展开/收起状态
  const [expandedItems, setExpandedItems] = useState<{
    [key: string]: boolean;
  }>({});
  // 跟踪每个 category item 的子分类展开/收起状态
  const [expandedSubCategories, setExpandedSubCategories] = useState<{
    [key: string]: boolean;
  }>({});
  // 跟踪每个筛选项的选中状态
  const [selectedItems, setSelectedItems] = useState<{ [key: string]: any }>(
    {}
  );
  // 根据 defaultSort 设置初始排序文案
  const getInitialSortByText = (): string => {
    if (!defaultSort?.sortBy) {
      return "Best Match";
    }

    const sortBy = defaultSort.sortBy.toLowerCase();
    switch (sortBy) {
      case "addtime":
        return "New Arrivals";
      case "popularity":
        return "Popularity";
      case "price":
        return "Price";
      case "clearance":
        return "BestSelling";
      case "free-shipping":
        return "BestSelling";
      case "bestmatch":
      default:
        return "Best Match";
    }
  };

  // 跟踪当前排序显示文案
  const [sortByText, setSortByText] = useState<string>(getInitialSortByText());

  // 根据 categoryNameFilter 初始化选中状态
  useEffect(() => {
    if (!categoryNameFilter || !sidebarFilter) return;

    // 找到 category 类型的筛选项
    const categoryFilter = sidebarFilter.find(
      (item) => item.key === "category"
    );
    if (!categoryFilter || !categoryFilter.items) return;

    // 递归查找匹配的分类项，并返回路径信息
    const findCategoryItem = (
      items: any[],
      parentItem?: any,
      parentIndex?: number
    ): { item: any; parentItem?: any; parentIndex?: number } | null => {
      for (let index = 0; index < items.length; index++) {
        const item = items[index];
        const itemName = (item.name || item.title || "").toLowerCase();
        if (itemName === categoryNameFilter) {
          return {
            item,
            parentItem,
            parentIndex: parentIndex !== undefined ? parentIndex : index,
          };
        }
        // 检查子分类
        if (item.subCategories && Array.isArray(item.subCategories)) {
          const found = findCategoryItem(item.subCategories, item, index);
          if (found) return found;
        }
      }
      return null;
    };

    const result = findCategoryItem(categoryFilter.items);
    if (result) {
      const { item: matchedItem, parentItem, parentIndex } = result;
      // 使用与 isItemSelected 相同的 key 生成逻辑
      const key = `category:${
        matchedItem.value ||
        matchedItem.name ||
        matchedItem.title ||
        JSON.stringify(matchedItem)
      }`;
      setSelectedItems({ [key]: matchedItem });

      // 展开 category 主分类
      setExpandedItems((prev) => ({
        ...prev,
        category: true,
      }));

      // 如果有父分类，展开父分类（使用与渲染时相同的 key 格式：category:${value || name || title || index}）
      if (parentItem && parentIndex !== undefined) {
        const parentKey = `category:${
          parentItem.value || parentItem.name || parentItem.title || parentIndex
        }`;
        setExpandedSubCategories((prev) => ({
          ...prev,
          [parentKey]: true,
        }));
      }
    }
  }, [categoryNameFilter, sidebarFilter]);

  // 切换筛选项的展开/收起状态
  const toggleExpanded = (key: string) => {
    setExpandedItems((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // 关闭弹窗的处理函数
  const handleClosePopup = () => {
    setVisible(false);
    // 重置所有展开状态
    setExpandedItems({});
    setExpandedSubCategories({});
  };

  // 切换子分类的展开/收起状态
  const toggleSubCategoryExpanded = (itemKey: string) => {
    setExpandedSubCategories((prev) => ({
      ...prev,
      [itemKey]: !prev[itemKey],
    }));
  };

  // 检查某个筛选项是否被选中
  const isItemSelected = (filterType: string, item: any) => {
    const key = `${filterType}:${
      item.value || item.name || item.title || JSON.stringify(item)
    }`;
    return selectedItems[key] !== undefined;
  };

  // 根据筛选项类型渲染不同的内容
  const renderFilterContent = (filterItem: any) => {
    const hasItems = filterItem.items && filterItem.items.length > 0;
    const isExpanded = expandedItems[filterItem.key];

    switch (filterItem.key) {
      case "category":
        // 如果 items 长度为 1，默认展开所有子分类；否则默认不展开
        const shouldExpandSubCategoriesByDefault =
          filterItem.items?.length === 1;

        return (
          <div className={styles["filter-box"]}>
            <div
              className={styles["header-box"]}
              onClick={() =>
                hasItems
                  ? toggleExpanded(filterItem.key)
                  : handleFilterOptionClick(filterItem.key, filterItem)
              }
            >
              <span>{filterItem.title}</span>
              {hasItems && (
                <span className={styles.expandArrow}>
                  {isExpanded ? (
                    <ArrowUp color="#333333" />
                  ) : (
                    <ArrowDown color="#333333" />
                  )}
                </span>
              )}
            </div>
            {hasItems && isExpanded && (
              <div className={styles.categoryItemsList}>
                {filterItem.items.map((item: any, index: number) => {
                  const itemKey = `${filterItem.key}:${
                    item.value || item.name || item.title || index
                  }`;
                  const hasChildren =
                    Array.isArray(item.subCategories) &&
                    item.subCategories.length > 0;
                  // 检查子分类是否展开：如果之前没有设置过，则使用默认值
                  const isSubCategoryExpanded =
                    expandedSubCategories[itemKey] !== undefined
                      ? expandedSubCategories[itemKey]
                      : shouldExpandSubCategoriesByDefault;

                  return (
                    <div key={itemKey} className={styles.categoryItemWrapper}>
                      <div
                        className={`${styles.categoryItem} ${
                          isItemSelected("category", item)
                            ? styles.selected
                            : ""
                        }`}
                        onClick={() =>
                          handleFilterOptionClick("category", item)
                        }
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <span>{item.name || item.title}</span>
                        {hasChildren && (
                          <span
                            className={styles.expandArrow}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleSubCategoryExpanded(itemKey);
                            }}
                            onMouseEnter={(e) => {
                              e.stopPropagation();
                            }}
                            onMouseLeave={(e) => {
                              e.stopPropagation();
                            }}
                            style={{
                              marginLeft: "auto",
                              pointerEvents: "auto",
                            }}
                          >
                            {isSubCategoryExpanded ? (
                              <ArrowUp color="#333333" />
                            ) : (
                              <ArrowDown color="#333333" />
                            )}
                          </span>
                        )}
                      </div>
                      {hasChildren && isSubCategoryExpanded && (
                        <div className={styles.categoryItemsList}>
                          {item.subCategories.map(
                            (child: any, childIndex: number) => (
                              <div
                                key={`${itemKey}:sub:${
                                  child.value ||
                                  child.name ||
                                  child.title ||
                                  childIndex
                                }`}
                                className={`${styles.categoryItem} ${styles.categorySubItem} ${
                                  isItemSelected("category", child)
                                    ? styles.selected
                                    : ""
                                }`}
                                onClick={() =>
                                  handleFilterOptionClick("category", child)
                                }
                              >
                                {child.name || child.title}
                              </div>
                            )
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );

      case "price-range":
        return (
          <div className={styles["filter-box"]}>
            <div
              className={styles["header-box"]}
              onClick={() =>
                hasItems
                  ? toggleExpanded(filterItem.key)
                  : handleFilterOptionClick(filterItem.key, filterItem)
              }
            >
              <span>{filterItem.title}</span>
              {hasItems && (
                <span className={styles.expandArrow}>
                  {isExpanded ? (
                    <ArrowUp color="#333333" />
                  ) : (
                    <ArrowDown color="#333333" />
                  )}
                </span>
              )}
            </div>
            {hasItems && isExpanded && (
              <div className={styles.priceRangeItemsList}>
                <div
                  className={styles.priceRangeItem}
                  onClick={() => {
                    handleSortClick({ type: "sort", value: "bestMatch" });
                    handleClosePopup();
                  }}
                >
                  View All
                </div>
                {filterItem.items.map((item: any, index: number) => (
                  <div
                    key={index}
                    className={`${styles.priceRangeItem} ${
                      isItemSelected(filterItem.key, item)
                        ? styles.selected
                        : ""
                    }`}
                    onClick={() =>
                      handleFilterOptionClick(filterItem.key, item)
                    }
                  >
                    {item.name || item.title}
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case "hot-brand":
        return (
          <div className={styles["filter-box"]}>
            <div
              className={styles["header-box"]}
              onClick={() =>
                hasItems
                  ? toggleExpanded(filterItem.key)
                  : handleFilterOptionClick(filterItem.key, filterItem)
              }
            >
              <span>{filterItem.title}</span>
              {hasItems && (
                <span className={styles.expandArrow}>
                  {isExpanded ? (
                    <ArrowUp color="#333333" />
                  ) : (
                    <ArrowDown color="#333333" />
                  )}
                </span>
              )}
            </div>
            {hasItems && isExpanded && (
              <div className={styles.brandItemsList}>
                <div
                  className={styles.brandItem}
                  onClick={() => {
                    handleSortClick({ type: "sort", value: "bestMatch" });
                    handleClosePopup();
                  }}
                >
                  View All
                </div>
                {filterItem.items.map((item: any, index: number) => (
                  <div
                    key={index}
                    className={`${styles.brandItem} ${
                      isItemSelected(filterItem.key, item)
                        ? styles.selected
                        : ""
                    }`}
                    onClick={() =>
                      handleFilterOptionClick(filterItem.key, item)
                    }
                  >
                    {item.name || item.title}
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case "popularity":
        return (
          <div className={styles["filter-box"]}>
            <div
              className={styles["header-box"]}
              onClick={() =>
                handleFilterOptionClick(filterItem.key, filterItem)
              }
            >
              <span>{filterItem.title}</span>
            </div>
          </div>
        );

      case "free-shipping":
        return (
          <div className={styles["filter-box"]}>
            <div
              className={styles["header-box"]}
              onClick={() =>
                handleFilterOptionClick("filter", { value: "freeshipping" })
              }
            >
              <span>{filterItem.title}</span>
            </div>
          </div>
        );

      default:
        return <div>Unknown filter type</div>;
    }
  };

  // 处理筛选选项点击
  const handleFilterOptionClick = (filterType: string, option: any) => {
    const key = `${filterType}:${
      option.value || option.name || option.title || JSON.stringify(option)
    }`;
    const isSelected = selectedItems[key] !== undefined;

    // 对于 free-shipping，需要特殊处理，确保 value 在顶层
    const filterOption: FilterOption =
      filterType === "filter" && option.value === "freeshipping"
        ? {
            type: filterType,
            value: option.value,
            item: option,
          }
        : {
            type: filterType,
            item: option,
          };

    // 清空顶部排序按钮的选择（handleSortClick 的选择）
    if (
      selectedFilter &&
      (selectedFilter.type === "sort" || selectedFilter.type === "filter")
    ) {
      setSelectedFilter(null);
    }

    // 更新选中状态 - 全局单选模式
    setSelectedItems((prev) => {
      const newState = { ...prev };

      if (isSelected) {
        // 如果已选中，则取消选中
        delete newState[key];
      } else {
        // 如果未选中，则选中
        // 先清除所有其他选中项（全局单选）
        Object.keys(newState).forEach((existingKey) => {
          delete newState[existingKey];
        });
        // 然后选中当前项
        newState[key] = option;
      }
      return newState;
    });

    // 调用父组件的回调函数
    onSortChange(filterOption);

    // 根据筛选类型设置不同的排序文案和选中状态
    if (filterType === "popularity") {
      // 在 new-arrivals 或 free-shipping 页面，
      // Popularity 视觉上选中 icon-all，但实际请求 popularity
      if (
        defaultSort?.sortBy?.toLowerCase() === "addtime" ||
        defaultSort?.sortBy?.toLowerCase() === "popularity"
      ) {
        setSortByText("Popularity");
        setSelectedFilter({ type: "sort", value: "bestMatch" });
      } else {
        // 其他页面，显示 Popularity
        setSortByText("Popularity");
        // 更新选中状态为 popularity（顶部按钮不会选中，因为type不是"sort"）
        setSelectedFilter(filterOption);
      }
    } else if (filterType === "filter" && option.value === "freeshipping") {
      // 点击 Free Shipping 时，显示 Free Shipping
      setSortByText("Free Shipping");
      // 更新选中状态为 filter（顶部按钮会选中）
      setSelectedFilter({ type: "filter", value: "freeshipping" });
    } else {
      // 其他筛选类型（category, price-range, hot-brand）使用默认排序 bestMatch
      setSortByText("Best Match");
      // 清空顶部排序按钮的选中状态（虽然使用bestMatch排序，但不显示为选中）
      setSelectedFilter(null);
    }

    // 关闭弹窗
    handleClosePopup();
  };

  const handleSortClick = (option: FilterOption) => {
    // 清空 Popup 中的筛选选择（handleFilterOptionClick 的选择）
    setSelectedItems({});

    // 在 new-arrivals 或 free-shipping 页面（defaultSort.sortBy 为 "addTime" 或 "popularity"），
    // 点击 icon-all 时，视觉上选中 icon-all，但实际请求 popularity
    if (
      option.type === "sort" &&
      option.value === "bestMatch" &&
      (defaultSort?.sortBy?.toLowerCase() === "addtime" ||
        defaultSort?.sortBy?.toLowerCase() === "popularity")
    ) {
      setSortByText("Popularity");
      setSelectedFilter({ type: "sort", value: "bestMatch" });
      onSortChange({ type: "popularity" });
      return;
    }

    // 价格排序的特殊处理：三态切换
    if (option.type === "sort" && option.value === "price") {
      let newPriceSort = null;

      if (
        !selectedFilter ||
        (selectedFilter.value !== "price-low-to-high" &&
          selectedFilter.value !== "price-high-to-low")
      ) {
        // 当前未选中价格排序，切换到低到高
        newPriceSort = { type: "sort", value: "price-low-to-high" };
      } else if (selectedFilter.value === "price-low-to-high") {
        // 当前是低到高，切换到高到低
        newPriceSort = { type: "sort", value: "price-high-to-low" };
      } else {
        // 当前是高到低，切换到低到高
        newPriceSort = { type: "sort", value: "price-low-to-high" };
      }

      setSelectedFilter(newPriceSort);
      setSortByText("Price");
      onSortChange(newPriceSort || { type: "sort", value: "price" });
      return;
    }

    // 更新排序文案
    if (option.value === "bestMatch") {
      // 如果有 defaultSort，使用 defaultSort 对应的文案，否则使用 "Best Match"
      setSortByText(getInitialSortByText());
    } else if (option.value === "new-arrivals") {
      setSortByText("New Arrivals");
    } else if (option.value === "freeshipping") {
      setSortByText("Free Shipping");
    }

    // 更新选中状态并触发回调
    setSelectedFilter(option);
    onSortChange(option);
  };

  return (
    <div className={styles["content-filter-box"]}>
      <div className={styles["content-filter"]}>
        <div className={styles["sort-title"]}>Sort by</div>
        <div className={styles["sort-by"]}>{sortByText}</div>
      </div>

      <div className={styles["sort-box"]}>
        <Image
          className={styles["sort-item"]}
          src={`${process.env.IMAGE_DOMAIN}/svg/category-page/icon-all${
            selectedFilter?.type === "sort" &&
            selectedFilter?.value === "bestMatch"
              ? "-red"
              : ""
          }.svg`}
          alt="filter"
          width={43}
          height={43}
          onClick={() => handleSortClick({ type: "sort", value: "bestMatch" })}
        />

        <Image
          className={styles["sort-item"]}
          src={`${process.env.IMAGE_DOMAIN}/svg/category-page/icon-price${
            selectedFilter?.value === "price-low-to-high"
              ? "-high-red"
              : selectedFilter?.value === "price-high-to-low"
              ? "-low-red"
              : ""
          }.svg`}
          alt="filter"
          width={43}
          height={43}
          onClick={() => handleSortClick({ type: "sort", value: "price" })}
        />

        <Image
          className={styles["sort-item"]}
          src={`${process.env.IMAGE_DOMAIN}/svg/category-page/icon-new${
            selectedFilter?.type === "sort" &&
            selectedFilter?.value === "new-arrivals"
              ? "-red"
              : ""
          }.svg`}
          alt="filter"
          width={43}
          height={43}
          onClick={() =>
            handleSortClick({ type: "sort", value: "new-arrivals" })
          }
        />

        {showFreeShipping && (
          <Image
            className={styles["sort-item"]}
            src={`${
              process.env.IMAGE_DOMAIN
            }/svg/category-page/icon-free-shipping${
              selectedFilter?.type === "filter" &&
              selectedFilter?.value === "freeshipping"
                ? "-red"
                : ""
            }.svg`}
            alt="filter"
            width={43}
            height={43}
            onClick={() =>
              handleSortClick({ type: "filter", value: "freeshipping" })
            }
          />
        )}
      </div>

      <Image
        src={`${process.env.IMAGE_DOMAIN}/svg/category-page/icon-filter.svg`}
        alt="Filter"
        className={styles["filter-icon"]}
        width={30}
        height={30}
        onClick={() => setVisible(true)}
        style={{ visibility: showFilter ? "visible" : "hidden" }}
      />

      <Popup
        visible={visible}
        closeable
        title="Filter"
        style={{ height: "80%" }}
        position="bottom"
        round
        onClose={handleClosePopup}
      >
        <div className={styles.filterContent}>
          {sidebarFilter?.map((filterItem, index) => (
            <div key={index} className={styles.filterSection}>
              {renderFilterContent(filterItem)}
            </div>
          ))}
          {showFreeShipping && (
            <div className={styles.filterSection}>
              {renderFilterContent({
                key: "free-shipping",
                title: "Free Shipping",
              })}
            </div>
          )}
        </div>
      </Popup>
    </div>
  );
}
